import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';

/**
 * Service for handling file downloads in the application.
 * Provides utility methods for downloading various file types with proper error handling.
 * 
 * Usage examples:
 * 
 * // Download a ZIP file
 * this.downloadService.downloadZip(blob, 'orders_invoice');
 * 
 * // Download an Excel file
 * this.downloadService.downloadExcel(blob, 'report_data');
 * 
 * // Download a PDF file
 * this.downloadService.downloadPdf(blob, 'document');
 * 
 * // Download with custom filename
 * this.downloadService.downloadWithCustomName(blob, 'custom_filename.pdf');
 * 
 * // Validate blob before download
 * if (this.downloadService.isValidBlob(blob)) {
 *   this.downloadService.downloadZip(blob, 'data');
 * }
 */
@Injectable({
    providedIn: 'root'
})
export class DownloadService {

    constructor() { }

    downloadBlob(blob: Blob, filename: string): void {
        try {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            throw new Error('Failed to download file');
        }
    }

    downloadZip(response: Blob | HttpResponse<any>, baseFilename: string = 'download'): void {
        if (response instanceof HttpResponse) {
            // Extract filename from content-disposition header
            let filename = `${baseFilename}_${new Date().getTime()}.zip`;
            const contentDisposition = response.headers.get('content-disposition');
            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (fileNameMatch && fileNameMatch[1]) {
                    filename = fileNameMatch[1].replace(/['"]/g, '');
                }
            }
            this.downloadBlob(response.body, filename);
        } else {
            // Fallback for Blob input
            const timestamp = new Date().getTime();
            const filename = `${baseFilename}_${timestamp}.zip`;
            this.downloadBlob(response, filename);
        }
    }

    downloadExcel(blob: Blob, baseFilename: string = 'download'): void {
        const timestamp = new Date().getTime();
        const filename = `${baseFilename}_${timestamp}.xlsx`;
        this.downloadBlob(blob, filename);
    }

    downloadPdf(response: Blob | HttpResponse<any>, baseFilename: string = 'download'): void {
        if (response instanceof HttpResponse) {
            // Extract filename from content-disposition header
            let filename = `${baseFilename}_${new Date().getTime()}.pdf`;
            const contentDisposition = response.headers.get('content-disposition');
            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (fileNameMatch && fileNameMatch[1]) {
                    filename = fileNameMatch[1].replace(/['"]/g, '');
                }
            }
            this.downloadBlob(response.body, filename);
        } else {
            // Fallback for Blob input
            const timestamp = new Date().getTime();
            const filename = `${baseFilename}_${timestamp}.pdf`;
            this.downloadBlob(response, filename);
        }
    }

    downloadCsv(blob: Blob, baseFilename: string = 'download'): void {
        const timestamp = new Date().getTime();
        const filename = `${baseFilename}_${timestamp}.csv`;
        this.downloadBlob(blob, filename);
    }

    downloadJson(blob: Blob, baseFilename: string = 'download'): void {
        const timestamp = new Date().getTime();
        const filename = `${baseFilename}_${timestamp}.json`;
        this.downloadBlob(blob, filename);
    }

    downloadWithCustomName(blob: Blob, filename: string): void {
        this.downloadBlob(blob, filename);
    }

    isValidBlob(blob: any): boolean {
        return blob instanceof Blob && blob.size > 0;
    }

    isValidResponse(response: any): boolean {
        if (response instanceof HttpResponse) {
            return response.body instanceof Blob && response.body.size > 0;
        }
        return this.isValidBlob(response);
    }
} 